import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { TradeForm } from "@components/pages/Futures/components/Controls/TradeForm";
import { MobileTradeForm } from "@components/pages/Futures/components/Controls/MobileTradeForm";
import { usePlaceTrade } from "@components/pages/Futures/components/Controls/TradeForm/hooks/usePlaceTrade";
import {
  LEVERAGE_DEFAULT,
  LEVERAGE_MAX,
  LEVERAGE_MIN,
  MIN_ORDER_COST_USDT,
} from "@components/pages/Futures/components/Controls/TradeForm/constants";

export const Controls = ({ onSuccess, isMobile = false }) => {
  const { price: currentPrice, pair } = useSelector((state) => state.futures);
  const active_wallet = useSelector((state) => state.wallet.active_wallet);
  const isBalanceLoading = useSelector((state) => state.wallet.loading);

  const [leverage, setLeverage] = useState(() => {
    const savedLeverage = localStorage.getItem("futuresLeverage");
    const parsedLeverage = Number(savedLeverage);

    return Number.isFinite(parsedLeverage) && parsedLeverage > 0
      ? parsedLeverage
      : LEVERAGE_DEFAULT;
  });

  const [longStats, setLongStats] = useState({
    max: 0,
    liqPrice: 0,
  });

  const [shortStats, setShortStats] = useState({
    max: 0,
    liqPrice: 0,
  });

  const [cost, setCost] = useState(String(MIN_ORDER_COST_USDT));
  const [takeProfitPercent, setTakeProfitPercent] = useState(null);
  const [stopLossPercent, setStopLossPercent] = useState(null);
  const [direction, setDirection] = useState("long");

  const availableBalance = useMemo(() => {
    const raw = active_wallet?.balance;

    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }

    const parsed = parseFloat(String(raw ?? "").replace(",", "."));

    return Number.isFinite(parsed) ? parsed : 0;
  }, [active_wallet?.balance, active_wallet?.id]);

  const numericCost =
    cost === "" ? 0 : parseFloat(String(cost).replace(",", ".")) || 0;

  const percentage = useMemo(
    () =>
      availableBalance > 0
        ? Math.min((numericCost / availableBalance) * 100, 100)
        : 0,
    [availableBalance, numericCost],
  );

  const commitWager = useCallback(
    (value) => {
      const numeric =
        typeof value === "number"
          ? value
          : parseFloat(String(value).replace(",", "."));
      const safe = Number.isFinite(numeric) ? numeric : 0;
      const rounded = Math.round(Math.max(safe, 0) * 100) / 100;

      setCost(rounded > 0 ? String(rounded) : "");
    },
    [],
  );

  const handleDirectionChange = (next) => {
    setDirection(next === "short" ? "short" : "long");
  };

  const handleLeverageChange = (next) => {
    const numeric = Math.trunc(Number(next));

    if (!Number.isFinite(numeric)) return;

    setLeverage(Math.min(LEVERAGE_MAX, Math.max(LEVERAGE_MIN, numeric)));
  };

  useEffect(() => {
    localStorage.setItem("futuresLeverage", String(leverage));
  }, [leverage]);

  useEffect(() => {
    const quantity =
      (availableBalance * (percentage / 100) * leverage) / currentPrice;

    const size = quantity * currentPrice;
    const initialMargin = size / leverage;

    const longLiqPrice = currentPrice * (1 - initialMargin / size);
    const shortLiqPrice = currentPrice * (1 + initialMargin / size);

    const max = availableBalance * leverage;

    setLongStats({
      max,
      liqPrice: longLiqPrice,
    });

    setShortStats({
      max,
      liqPrice: shortLiqPrice,
    });
  }, [availableBalance, currentPrice, leverage, percentage]);

  const { isPlacingTrade, handlePlaceTrade } = usePlaceTrade({
    currentPrice,
    pair,
    availableBalance,
    cost,
    numericCost,
    percentage,
    leverage,
    takeProfitPercent,
    stopLossPercent,
    direction,
    onSuccess,
  });

  const activeStats = direction === "short" ? shortStats : longStats;
  const Form = isMobile ? MobileTradeForm : TradeForm;

  return (
    <Form
      pair={pair}
      direction={direction}
      onDirectionChange={handleDirectionChange}
      leverage={leverage}
      onLeverageChange={handleLeverageChange}
      wager={numericCost}
      onWagerChange={commitWager}
      availableBalance={availableBalance}
      liquidationPrice={activeStats.liqPrice}
      entryPrice={currentPrice}
      setTakeProfitPercent={setTakeProfitPercent}
      setStopLossPercent={setStopLossPercent}
      onPlaceTrade={handlePlaceTrade}
      isPlacingTrade={isPlacingTrade}
      isBalanceLoading={isBalanceLoading}
    />
  );
};
